// ActionPlanModal.js - VERSION CORRIGÉE (gère création ET modification)
import {
    Check,
    Plus,
    Target,
    X
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import TaskList from './TaskList';

const ActionPlanModal = ({ 
    isOpen, 
    onClose, 
    onSave, 
    objective, 
    editPlan = null,
    plansCachedHook 
}) => {
    const [formData, setFormData] = useState({ tasks: [] });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const modalRef = useRef(null);

    // Extraire les fonctions du hook passé en props
    const { 
        createPlan,
        updatePlan, 
        loading: backendLoading, 
        error: backendError,
        invalidateCache
    } = plansCachedHook || {};

    useEffect(() => {
        if (isOpen && objective) {
            if (editPlan) {
                // Plan existant : charger les données
                let tasks = [];
                
                // Gérer les différents formats de tâches
                if (editPlan.tasks) {
                    if (typeof editPlan.tasks === 'string') {
                        try {
                            tasks = JSON.parse(editPlan.tasks);
                        } catch (e) {
                            console.warn('❌ Erreur parsing tasks editPlan:', e);
                            tasks = [];
                        }
                    } else if (Array.isArray(editPlan.tasks)) {
                        tasks = editPlan.tasks;
                    } else if (typeof editPlan.tasks === 'object' && editPlan.tasks !== null) {
                        tasks = Object.values(editPlan.tasks);
                    }
                }

                setFormData({
                    tasks: tasks.map(task => ({
                        ...task,
                        id: task.id || Date.now() + Math.random(),
                        isExpanded: false
                    }))
                });
            } else {
                // Pas de plan existant : commencer avec une liste vide
                console.log('🆕 Aucun plan existant, création d\'un nouveau plan');
                setFormData({
                    tasks: []
                });
            }
            setErrors({});
            
            // Force l'état collapsed après un court délai
            setTimeout(() => {
                setFormData(prev => ({
                    ...prev,
                    tasks: prev.tasks.map(task => ({ ...task, isExpanded: false }))
                }));
            }, 100);
        }
    }, [isOpen, objective, editPlan]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            // Ne pas fermer si c'est un dropdown de statut
            if (e.target.closest('[data-status-dropdown]')) {
                return;
            }
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose();
            }
        };
        
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen, onClose]);

    // Afficher les erreurs backend
    useEffect(() => {
        if (backendError) {
            setErrors(prev => ({
                ...prev,
                backend: typeof backendError === 'string' ? backendError : backendError.message || 'Erreur backend'
            }));
        }
    }, [backendError]);

    function createNewTask(title = '', priority = 'medium') {
        return {
            id: Date.now() + Math.random(),
            title,
            priority,
            deadline: '',
            status: 'a_faire',
            subtasks: [],
            isExpanded: false,
            description: '',
            estimated_hours: null
        };
    }

    function addTask() {
        const newTask = createNewTask(`Action ${formData.tasks.length + 1}`);
        setFormData(prev => ({
            ...prev,
            tasks: [...prev.tasks, newTask]
        }));
    }

    function updateTasks(newTasks) {
        setFormData(prev => ({
            ...prev,
            tasks: newTasks
        }));
    }

    function validateForm() {
        const newErrors = {};
        
        formData.tasks.forEach((task, taskIndex) => {
            if (!task.title || !task.title.trim()) {
                newErrors[`task_${taskIndex}_title`] = 'Le titre de la tâche est requis';
            }
            
            // Validation des sous-tâches
            if (task.subtasks && Array.isArray(task.subtasks)) {
                task.subtasks.forEach((subtask, subtaskIndex) => {
                    if (!subtask.title || !subtask.title.trim()) {
                        newErrors[`task_${taskIndex}_subtask_${subtaskIndex}_title`] = 'Le titre de la sous-tâche est requis';
                    }
                });
            }
        });

        // Validation pour s'assurer qu'il y a au moins une tâche
        if (formData.tasks.length === 0) {
            newErrors.tasks = 'Au moins une tâche est requise';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    // Conversion des tâches au format backend
    const convertTasksToBackendFormat = (tasks) => {
        return tasks.map((task, index) => {
            // Assurer la présence de TOUS les champs requis
            const backendTask = {
                title: task.title?.trim() || `Action ${index + 1}`,
                description: task.description?.trim() || '',
                status: task.status || 'a_faire',
                priority: task.priority || 'medium',
                deadline: task.deadline && task.deadline !== '' ? task.deadline : null,
                estimated_hours: task.estimated_hours && !isNaN(parseFloat(task.estimated_hours)) ? parseFloat(task.estimated_hours) : null,
                ordre_affichage: index
            };

            // Traiter les sous-tâches si elles existent
            if (task.subtasks && Array.isArray(task.subtasks) && task.subtasks.length > 0) {
                backendTask.subtasks = task.subtasks.map((subtask, subIndex) => ({
                    title: subtask.title?.trim() || `Sous-tâche ${subIndex + 1}`,
                    description: subtask.description?.trim() || '',
                    status: subtask.status || 'a_faire',
                    priority: subtask.priority || 'medium',
                    deadline: subtask.deadline && subtask.deadline !== '' ? subtask.deadline : null,
                    estimated_hours: subtask.estimated_hours && !isNaN(parseFloat(subtask.estimated_hours)) ? parseFloat(subtask.estimated_hours) : null,
                    ordre_affichage: subIndex
                }));
            } else {
                backendTask.subtasks = [];
            }

            return backendTask;
        });
    };

    async function handleSubmit(e) {
        e.preventDefault();
        
        if (!validateForm()) {
            console.log('❌ Validation échouée:', errors);
            return;
        }

        // Vérifier que les fonctions nécessaires sont disponibles
        if (!createPlan || !updatePlan) {
            setErrors(prev => ({
                ...prev,
                backend: 'Fonctions de sauvegarde non disponibles'
            }));
            return;
        }

        setIsSubmitting(true);
        setErrors(prev => ({ ...prev, backend: null }));

        try {
            // Validation de l'objectif
            if (!objective) {
                throw new Error('Objectif manquant');
            }

            // Gestion robuste de l'ID objectif
            let objectifId;
            if (objective.id_objectif) {
                objectifId = objective.id_objectif;
            } else if (objective.id && typeof objective.id === 'string' && objective.id.includes('-')) {
                objectifId = objective.id;
            } else {
                throw new Error('ID objectif UUID manquant. Structure reçue: ' + JSON.stringify(Object.keys(objective)));
            }

            // Préparation des données pour l'API backend
            // Générer un titre unique avec UUID court
            const shortUuid = crypto.randomUUID().split('-')[0];
            const backendData = {
                title: isCreating 
                    ? `Plan d'action - ${objective.category || objective.title} - ${shortUuid}`
                    : editPlan.title || `Plan d'action - ${objective.category || objective.title}`,
                description: `Plan pour atteindre l'objectif "${objective.title || 'Sans titre'}" - Valeur cible: ${(objective.target_value || objective.target || 0).toLocaleString('fr-FR')} en ${objective.timeline || 'N/A'} ${objective.timeline_unit || 'mois'}`,
                id_objectif: objectifId,
                priority: editPlan?.priority || 'medium',
                start_date: editPlan?.start_date || null,
                target_date: editPlan?.target_date || null,
                ordre_affichage: editPlan?.ordre_affichage || 0,
                tasks: convertTasksToBackendFormat(formData.tasks)
            };

            console.log('📤 Données envoyées au backend:', backendData);

            let result;
            
            if (editPlan && editPlan.id_plan) {
                // Cas 1: Plan existant - Modification
                console.log('✏️ Modification du plan existant:', editPlan.id_plan);
                result = await updatePlan(editPlan.id_plan, backendData);
            } else {
                // Cas 2: Nouveau plan - Création
                console.log('🆕 Création d\'un nouveau plan pour l\'objectif:', objectifId);
                result = await createPlan(backendData);
            }

            console.log('✅ Réponse du backend:', result);

            if (!result.success) {
                throw new Error(result.error || 'Erreur lors de la sauvegarde');
            }

            // Invalider le cache après succès
            if (invalidateCache) {
                invalidateCache();
                console.log('🗑️ Cache invalidé après sauvegarde');
            }

            // Préparer les données pour la compatibilité avec l'ancien code
            const planData = result.plan || result.plan_action;
            const actionPlanData = {
                ...planData,
                objectiveId: objectifId,
                objectiveCategory: objective.category,
                createdAt: planData?.created_at || new Date().toISOString(),
                status: planData?.status || 'a_faire',
                tasks: formData.tasks,
                taches_creees: result.taches_creees || formData.tasks.length
            };

            // Appel du callback parent (pour compatibilité)
            if (onSave) {
                await onSave(actionPlanData);
            }

            console.log('✅ Plan d\'action sauvegardé avec succès');
            onClose();

        } catch (error) {
            console.error('❌ Erreur lors de la sauvegarde:', error);
            setErrors(prev => ({
                ...prev,
                backend: error.message || 'Une erreur est survenue lors de la sauvegarde'
            }));
        } finally {
            setIsSubmitting(false);
        }
    }

    if (!isOpen) return null;

    // Déterminer si on est en mode création ou modification
    const isCreating = !editPlan || !editPlan.id_plan;

    return (
        <div className="fixed inset-0 z-[9999] overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-30 backdrop-blur-sm" />
                
                <div
                    ref={modalRef}
                    className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-lg rounded-lg max-h-[90vh] overflow-y-auto border border-gray-200"
                >
                    {/* Header */}
                    <div className="bg-gray-100 px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    {isCreating ? 'Créer le plan d\'action' : 'Modifier le plan d\'action'}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    {objective?.category || 'Catégorie'} • {(objective?.current_value || objective?.current || 0).toLocaleString('fr-FR')} → {(objective?.target_value || objective?.target || 0).toLocaleString('fr-FR')}
                                </p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md transition-colors"
                                disabled={isSubmitting || backendLoading}
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Affichage des erreurs backend */}
                    {errors.backend && (
                        <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <div className="text-sm text-red-700">
                                <strong>Erreur de sauvegarde:</strong> {errors.backend}
                            </div>
                        </div>
                    )}

                    <div className="p-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Section des tâches */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h4 className="text-sm font-medium text-gray-800">
                                        Actions et sous-actions
                                    </h4>
                                    <button
                                        type="button"
                                        onClick={addTask}
                                        className="flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 rounded-md transition-all duration-200 hover:scale-105"
                                        style={{ backgroundColor: '#c8d7d6' }}
                                        disabled={isSubmitting || backendLoading}
                                    >
                                        <Plus className="w-4 h-4 mr-1.5" />
                                        Ajouter une action
                                    </button>
                                </div>

                                {/* Message d'aide si pas de tâches */}
                                {formData.tasks.length === 0 && (
                                    <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                        <div className="text-gray-500 mb-3">
                                            <svg className="w-12 h-12 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                                            </svg>
                                        </div>
                                        <p className="text-sm text-gray-600 mb-4">Aucune action créée pour le moment</p>
                                        <button
                                            type="button"
                                            onClick={addTask}
                                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 rounded-md transition-all duration-200 hover:scale-105"
                                            style={{ backgroundColor: '#c8d7d6' }}
                                            disabled={isSubmitting || backendLoading}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Créer ma première action
                                        </button>
                                    </div>
                                )}

                                {/* Erreur si pas de tâches */}
                                {errors.tasks && (
                                    <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                                        {errors.tasks}
                                    </div>
                                )}

                                {/* Liste des tâches */}
                                {formData.tasks.length > 0 && (
                                    <div className="space-y-3">
                                        <TaskList
                                            tasks={formData.tasks}
                                            onUpdateTasks={updateTasks}
                                            errors={errors}
                                            defaultExpanded={false}
                                            disabled={isSubmitting || backendLoading}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-md hover:bg-gray-200 transition-colors"
                                    disabled={isSubmitting || backendLoading}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || backendLoading || formData.tasks.length === 0}
                                    className="px-6 py-2 text-sm font-medium text-gray-700 rounded-md transition-all duration-200 disabled:opacity-50 flex items-center hover:scale-105"
                                    style={{ backgroundColor: '#c8d7d6' }}
                                >
                                    {(isSubmitting || backendLoading) ? (
                                        <>
                                            <div className="w-4 h-4 mr-2 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                                            {isCreating ? 'Création...' : 'Sauvegarde...'}
                                        </>
                                    ) : (
                                        <>
                                            <Check className="w-4 h-4 mr-2" />
                                            {isCreating ? 'Créer le plan' : 'Sauvegarder le plan'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ActionPlanModal;